/*
 * tt-product.js — behaviour for the compact single-card product page
 * (sections/tt-product.liquid). Progressive enhancement only; the page is
 * fully readable and add-to-cart works (Dawn <product-form>) without this.
 *
 * Handles, scoped per [data-tt-pdp] root:
 *   - gallery thumbnail swap (front / back / …) + active highlight
 *   - click-to-enlarge lightbox (open on main image, close on backdrop / × / Esc)
 *   - quantity stepper bound to the form's name="quantity" input (clamped 1..max)
 *   - "Buy it now" → add current variant+qty, then go to checkout
 *   - multi-variant <select> fallback (sealed/accessories): updates the hidden
 *     variant id, price, stock line, qty max and the Add/Buy button state.
 *
 * No product/card markup is built here (frontend rule 1) — related cards are
 * server-rendered in Liquid and carry data-tt-quickadd for tt-quick-actions.js.
 */
(function () {
  function init(root) {
    if (root.ttPdpWired) return;
    root.ttPdpWired = true;

    upgradeRelated(root);

    var threshold = parseInt(root.getAttribute('data-threshold'), 10);
    if (isNaN(threshold)) threshold = 10;

    /* ── Gallery + lightbox ─────────────────────────────────────────── */
    var mainImg = root.querySelector('[data-tt-main-img]');
    var mainWell = root.querySelector('[data-tt-main-well]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-tt-thumb]'));
    var lightbox = root.querySelector('[data-tt-lightbox]');
    var lightboxImg = root.querySelector('[data-tt-lightbox-img]');
    var lightboxClose = root.querySelector('[data-tt-lightbox-close]');

    function selectThumb(thumb) {
      if (!thumb || !mainImg) return;
      var full = thumb.getAttribute('data-full');
      var zoom = thumb.getAttribute('data-zoom');
      if (full) {
        mainImg.src = full;
        mainImg.removeAttribute('srcset');
      }
      if (zoom) mainImg.setAttribute('data-zoom-src', zoom);
      thumbs.forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
    }
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () { selectThumb(thumb); });
    });

    // next/prev image (driven by swipe on the main well)
    var suppressClick = false;
    function currentThumbIndex() {
      for (var i = 0; i < thumbs.length; i++) {
        if (thumbs[i].classList.contains('is-active')) return i;
      }
      return 0;
    }
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var animating = false;
    var DUR = 300;
    var EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)'; // ease-out, smooth settle
    function slideImage(dir) {
      if (thumbs.length < 2 || animating || !mainImg || !mainWell) return;
      var i = currentThumbIndex() + dir;
      if (i < 0) i = thumbs.length - 1;
      if (i >= thumbs.length) i = 0;
      var thumb = thumbs[i];
      var full = thumb.getAttribute('data-full');
      var zoom = thumb.getAttribute('data-zoom');
      if (reduceMotion || !full) { selectThumb(thumb); return; }
      animating = true;

      // Preload the next image first so the incoming layer never flashes blank.
      var pre = new Image();
      var started = false;
      function run() {
        if (started) return; started = true;

        // Incoming image as an overlay, off-screen on the side we're swiping from.
        var incoming = mainImg.cloneNode(false);
        incoming.removeAttribute('data-tt-main-img');
        incoming.removeAttribute('srcset');
        incoming.src = full;
        incoming.style.position = 'absolute';
        incoming.style.inset = '0';
        incoming.style.transition = 'none';
        incoming.style.transform = 'translateX(' + (dir > 0 ? '100%' : '-100%') + ')';
        mainWell.appendChild(incoming);

        // Commit start states, then cross-slide BOTH layers at once (no empty gap).
        void incoming.offsetWidth;
        mainImg.style.transition = 'transform ' + DUR + 'ms ' + EASE;
        incoming.style.transition = 'transform ' + DUR + 'ms ' + EASE;
        mainImg.style.transform = 'translateX(' + (dir > 0 ? '-100%' : '100%') + ')';
        incoming.style.transform = 'translateX(0)';

        window.setTimeout(function () {
          // Settle: real main img becomes the new image in place; drop the overlay.
          mainImg.src = full;
          mainImg.removeAttribute('srcset');
          if (zoom) mainImg.setAttribute('data-zoom-src', zoom);
          thumbs.forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
          mainImg.style.transition = 'none';
          mainImg.style.transform = '';
          if (incoming.parentNode) incoming.parentNode.removeChild(incoming);
          void mainImg.offsetWidth;
          mainImg.style.transition = '';
          animating = false;
        }, DUR + 20);
      }
      pre.onload = run;
      pre.onerror = run;
      pre.src = full;
      if (pre.complete) run();
    }

    function openLightbox() {
      if (!lightbox || !lightboxImg || !mainImg) return;
      lightboxImg.src = mainImg.getAttribute('data-zoom-src') || mainImg.src;
      lightbox.hidden = false;
      document.documentElement.style.overflow = 'hidden';
    }
    function closeLightbox() {
      if (!lightbox) return;
      lightbox.hidden = true;
      document.documentElement.style.overflow = '';
    }
    if (mainWell) {
      mainWell.addEventListener('click', function () {
        if (suppressClick) { suppressClick = false; return; }
        openLightbox();
      });
      mainWell.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLightbox(); }
      });
      // Swipe left/right on the main image to browse front / back / … (touch)
      var swStartX = 0, swStartY = 0;
      mainWell.addEventListener('touchstart', function (e) {
        var t = e.changedTouches[0]; swStartX = t.clientX; swStartY = t.clientY;
      }, { passive: true });
      mainWell.addEventListener('touchend', function (e) {
        var t = e.changedTouches[0];
        var dx = t.clientX - swStartX, dy = t.clientY - swStartY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          suppressClick = true;          // a swipe shouldn't open the lightbox
          slideImage(dx < 0 ? 1 : -1);   // swipe left = next, right = prev (animated)
        }
      }, { passive: true });
    }
    if (lightbox) {
      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox || e.target === lightboxClose) closeLightbox();
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && lightbox && !lightbox.hidden) closeLightbox();
    });

    /* ── Quantity stepper ───────────────────────────────────────────── */
    var qtyInput = root.querySelector('[data-tt-qty]');
    function clampQty() {
      if (!qtyInput) return;
      var min = parseInt(qtyInput.min, 10) || 1;
      var max = qtyInput.max ? parseInt(qtyInput.max, 10) : Infinity;
      var val = parseInt(qtyInput.value, 10);
      if (isNaN(val)) val = min;
      val = Math.max(min, Math.min(max, val));
      qtyInput.value = val;
    }
    root.querySelectorAll('[data-tt-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var step = parseInt(btn.getAttribute('data-tt-step'), 10) || 0;
        qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + step;
        clampQty();
      });
    });
    if (qtyInput) qtyInput.addEventListener('change', clampQty);

    /* ── Multi-variant fallback ─────────────────────────────────────── */
    var variantSelect = root.querySelector('[data-tt-variant-select]');
    var idInput = root.querySelector('[data-tt-variant-id]');
    var priceEl = root.querySelector('[data-tt-price]');
    var addBtn = root.querySelector('[data-tt-add]');
    var addLabel = root.querySelector('[data-tt-add-label]');
    var buyBtn = root.querySelector('[data-tt-buynow]');

    function applyVariant(opt) {
      if (!opt) return;
      var id = opt.value;
      var price = opt.getAttribute('data-price');
      var available = opt.getAttribute('data-available') === 'true';
      var tracked = opt.getAttribute('data-tracked') === 'true';
      var inv = parseInt(opt.getAttribute('data-inventory'), 10);

      if (idInput) {
        idInput.value = id;
        if (available) { idInput.removeAttribute('disabled'); } else { idInput.setAttribute('disabled', ''); }
      }
      if (priceEl && price) priceEl.textContent = price;

      // qty max follows the chosen variant's stock
      if (qtyInput) {
        if (tracked && inv > 0) { qtyInput.max = inv; } else { qtyInput.removeAttribute('max'); }
        clampQty();
      }

      // stock line
      var stockText = root.querySelector('.tt-pdp__stock-text');
      var stockDot = root.querySelector('.tt-pdp__stock-dot');
      if (stockText && stockDot) {
        if (available) {
          stockDot.classList.remove('tt-pdp__stock-dot--out');
          stockText.classList.remove('tt-pdp__stock-text--out');
          if (tracked && inv > 0) {
            stockText.textContent = inv > threshold
              ? 'In stock — ' + threshold + '+ available'
              : 'In stock — ' + inv + ' available';
          } else {
            stockText.textContent = 'In stock';
          }
        } else {
          stockDot.classList.add('tt-pdp__stock-dot--out');
          stockText.classList.add('tt-pdp__stock-text--out');
          stockText.textContent = 'Out of stock';
        }
      }

      // button states
      [addBtn, buyBtn].forEach(function (b) {
        if (!b) return;
        if (available) { b.removeAttribute('disabled'); } else { b.setAttribute('disabled', ''); }
      });
      if (addLabel) addLabel.textContent = available ? 'Add to cart' : 'Sold out';
    }
    if (variantSelect) {
      variantSelect.addEventListener('change', function () {
        applyVariant(variantSelect.options[variantSelect.selectedIndex]);
      });
    }

    /* ── Buy it now → add then checkout ─────────────────────────────── */
    if (buyBtn && idInput) {
      buyBtn.addEventListener('click', function () {
        if (buyBtn.hasAttribute('disabled')) return;
        var id = idInput.value;
        var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
        buyBtn.classList.add('is-loading');
        buyBtn.setAttribute('disabled', '');
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: id, quantity: qty })
        }).then(function (r) {
          if (!r.ok) throw new Error('add failed');
          window.location.href = '/checkout';
        }).catch(function () {
          buyBtn.classList.remove('is-loading');
          buyBtn.removeAttribute('disabled');
        });
      });
    }
  }

  /* ── Suggested products: upgrade "More from <game>" to other cards of the SAME
     NAME via Shopify Search + the Section Rendering API. Cards are server-rendered
     (frontend rule 1); if search returns nothing we keep the game-based strip. */
  function upgradeRelated(root) {
    var rel = root.querySelector('.tt-pdp__related');
    if (!rel) return;
    var grid = rel.querySelector('.tt-pdp__related-grid');
    if (!grid) return;
    var excludeHandle = rel.getAttribute('data-tt-related-handle');
    var count = parseInt(rel.getAttribute('data-tt-related-count'), 10) || 6;
    var name = rel.getAttribute('data-tt-related-name') || '';
    var nameQ = (rel.getAttribute('data-tt-related-query') || '').trim();
    var setQ = (rel.getAttribute('data-tt-related-set') || '').trim();
    if (!nameQ && !setQ) return;

    function search(q) {
      if (!q) return Promise.resolve([]);
      var url = '/search?type=product&q=' + encodeURIComponent(q) + '&section_id=tt-related-search';
      return fetch(url, { headers: { 'Accept': 'text/html' } })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          return Array.prototype.slice.call(tmp.querySelectorAll('li.grid__item'))
            .filter(function (li) { return li.getAttribute('data-tt-rcard-handle') !== excludeHandle; });
        })
        .catch(function () { return []; });
    }

    /* Tiered, NOT mixed: if there are same-name cards, show only those (however
       many). Otherwise fall back to same-set (so unique-name cards still vary per
       product). Otherwise keep the server-rendered game strip. A named card must
       never be padded with other-name cards from its set. */
    Promise.all([search(nameQ), search(setQ)]).then(function (res) {
      var nameItems = res[0], setItems = res[1];
      var chosen, label, vq;
      if (nameItems.length) {
        chosen = nameItems; label = 'More ' + name + ' cards'; vq = nameQ;
      } else if (setItems.length) {
        chosen = setItems; label = 'More from ' + setQ; vq = setQ;
      } else {
        return;                                       /* keep the game fallback */
      }
      chosen = chosen.slice(0, count);
      grid.innerHTML = '';
      chosen.forEach(function (li) { grid.appendChild(li); });

      var title = rel.querySelector('.tt-pdp__related-title');
      if (title) title.textContent = label;
      var viewall = rel.querySelector('.tt-pdp__related-viewall');
      if (viewall) viewall.setAttribute('href', '/search?type=product&q=' + encodeURIComponent(vq));
    });
  }

  function boot() {
    document.querySelectorAll('[data-tt-pdp]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
